import { useEffect, useMemo, useState, type FormEvent } from 'react'
import AdminShell from './AdminShell'
import {
  AdminButton,
  AdminCard,
  AdminDataTable,
  AdminField,
  AdminFilterBar,
  AdminInput,
  AdminModal,
  AdminPagination,
  AdminSelect,
  AdminSkeleton,
  AdminStatePanel,
  AdminStatusPill,
  AdminToastRegion,
  type AdminTableColumn,
  type AdminToast,
} from './AdminUi'
import '../styles/admin-discount-codes.css'

type SessionPayload = {
  authenticated: true
  user: { email: string; role: string }
  csrfToken: string
}

type DiscountStatus = 'Active' | 'Scheduled' | 'Inactive' | 'Expired' | 'Exhausted' | 'Archived'
type DiscountType = 'fixed' | 'percentage'

type DiscountCode = {
  id: string
  code: string
  discountType: DiscountType
  fixedAmountCents: number | null
  percentageBasisPoints: number | null
  minimumSubtotalCents: number | null
  startsAt: string | null
  endsAt: string | null
  firstOrderOnly: boolean
  perCustomerLimit: number | null
  totalRedemptionLimit: number | null
  enabled: boolean
  archivedAt: string | null
  version: number
  createdAt: string
  updatedAt: string
  status: DiscountStatus
  offer: string
  eligibility: string
  uses: number
  activeUses: number
  remainingUses: number | null
  totalDiscountAppliedCents: number
  appliesTo: 'Product subtotal'
}

type Summary = {
  activeCodes: number
  usesThisWeek: number
  discountAppliedThisWeekCents: number
  firstOrderUsesThisWeek: number
  week: { startsAt: string; endsAt: string; timeZone: 'Pacific/Auckland' }
}

type ListPayload = {
  summary: Summary
  page: number
  total: number
  totalPages: number
  codes: DiscountCode[]
}

type Redemption = {
  id: string
  orderId: string | null
  customerName: string
  discountCents: number
  orderTotalCents: number
  paymentStatus: string | null
  committedAt: string | null
}

type DetailPayload = { code: DiscountCode; recentRedemptions: Redemption[] }

type Draft = {
  code: string
  discountType: DiscountType
  fixedAmount: string
  percentage: string
  minimumSubtotal: string
  startsAt: string
  endsAt: string
  firstOrderOnly: boolean
  perCustomerLimit: string
  totalRedemptionLimit: string
}

type PendingAction =
  | { kind: 'activate' | 'pause' | 'archive'; code: DiscountCode }
  | { kind: 'save-active'; code: DiscountCode; draft: Draft; changes: string[] }
  | null

const NZD = new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD', minimumFractionDigits: 2 })
const DATE_ONLY = new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric' })
const DATE_TIME = new Intl.DateTimeFormat('en-NZ', { timeZone: 'Pacific/Auckland', day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })

function money(cents: number) { return NZD.format(cents / 100) }
function date(value: string | null) {
  if (!value) return 'No limit'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_ONLY.format(parsed)
}
function dateTime(value: string | null) {
  if (!value) return 'No limit'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : DATE_TIME.format(parsed)
}
function environmentFromHostname(hostname: string) {
  const host = hostname.toLowerCase()
  if (host.includes('staging')) return 'Staging'
  if (host === 'localhost' || host === '127.0.0.1') return 'Local'
  return undefined
}
function statusTone(status: DiscountStatus): 'success' | 'info' | 'neutral' | 'warning' | 'danger' {
  if (status === 'Active') return 'success'
  if (status === 'Scheduled') return 'info'
  if (status === 'Expired' || status === 'Exhausted') return 'warning'
  if (status === 'Archived') return 'danger'
  return 'neutral'
}
function localInputValue(value: string | null) {
  if (!value) return ''
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return ''
  const parts = new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  }).formatToParts(parsed)
  const fields = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return `${fields.year}-${fields.month}-${fields.day}T${fields.hour}:${fields.minute}`
}
function emptyDraft(): Draft {
  return {
    code: '', discountType: 'fixed', fixedAmount: '', percentage: '', minimumSubtotal: '',
    startsAt: '', endsAt: '', firstOrderOnly: false, perCustomerLimit: '', totalRedemptionLimit: '',
  }
}
function draftFromCode(code: DiscountCode): Draft {
  return {
    code: code.code,
    discountType: code.discountType,
    fixedAmount: code.fixedAmountCents === null ? '' : (code.fixedAmountCents / 100).toFixed(2),
    percentage: code.percentageBasisPoints === null ? '' : String(code.percentageBasisPoints / 100),
    minimumSubtotal: code.minimumSubtotalCents === null ? '' : (code.minimumSubtotalCents / 100).toFixed(2),
    startsAt: localInputValue(code.startsAt),
    endsAt: localInputValue(code.endsAt),
    firstOrderOnly: code.firstOrderOnly,
    perCustomerLimit: code.perCustomerLimit === null ? '' : String(code.perCustomerLimit),
    totalRedemptionLimit: code.totalRedemptionLimit === null ? '' : String(code.totalRedemptionLimit),
  }
}
function decimalCents(value: string, allowBlank: boolean) {
  const trimmed = value.trim()
  if (!trimmed && allowBlank) return null
  const match = /^(\d+)(?:\.(\d{0,2}))?$/.exec(trimmed)
  if (!match) return undefined
  const cents = Number(match[1]) * 100 + Number((match[2] ?? '').padEnd(2, '0') || '0')
  return Number.isSafeInteger(cents) ? cents : undefined
}
function positiveInteger(value: string, allowBlank = true) {
  const trimmed = value.trim()
  if (!trimmed && allowBlank) return null
  const parsed = Number(trimmed)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined
}
function payloadFromDraft(draft: Draft) {
  const fixedAmountCents = draft.discountType === 'fixed' ? decimalCents(draft.fixedAmount, false) : null
  const percent = draft.discountType === 'percentage' ? Number(draft.percentage.trim()) : null
  const percentageBasisPoints = percent === null ? null : Number.isFinite(percent) ? Math.round(percent * 100) : undefined
  const minimumSubtotalCents = decimalCents(draft.minimumSubtotal, true)
  const perCustomerLimit = positiveInteger(draft.perCustomerLimit)
  const totalRedemptionLimit = positiveInteger(draft.totalRedemptionLimit)
  const valid = fixedAmountCents !== undefined
    && fixedAmountCents !== 0
    && percentageBasisPoints !== undefined
    && (percentageBasisPoints === null || (percentageBasisPoints > 0 && percentageBasisPoints <= 10000))
    && minimumSubtotalCents !== undefined
    && perCustomerLimit !== undefined
    && totalRedemptionLimit !== undefined
    && (!draft.startsAt || !Number.isNaN(new Date(draft.startsAt).getTime()))
    && (!draft.endsAt || !Number.isNaN(new Date(draft.endsAt).getTime()))
  if (!valid) return null
  return {
    code: draft.code.trim().toUpperCase(),
    discountType: draft.discountType,
    fixedAmountCents,
    percentageBasisPoints,
    minimumSubtotalCents,
    startsAt: draft.startsAt || null,
    endsAt: draft.endsAt || null,
    firstOrderOnly: draft.firstOrderOnly,
    perCustomerLimit,
    totalRedemptionLimit,
  }
}
function offerFromDraft(draft: Draft) {
  if (draft.discountType === 'fixed') return draft.fixedAmount ? `${draft.fixedAmount} fixed` : 'Fixed amount'
  return draft.percentage ? `${draft.percentage}%` : 'Percentage'
}
function changesFor(code: DiscountCode, draft: Draft) {
  const before = draftFromCode(code)
  const changes: string[] = []
  const pairs: Array<[keyof Draft, string]> = [
    ['discountType', 'Discount type'], ['fixedAmount', 'Fixed amount'], ['percentage', 'Percentage'],
    ['minimumSubtotal', 'Minimum spend'], ['startsAt', 'Starts'], ['endsAt', 'Ends'],
    ['firstOrderOnly', 'First order only'], ['perCustomerLimit', 'Per-customer limit'], ['totalRedemptionLimit', 'Total usage limit'],
  ]
  for (const [key, label] of pairs) {
    if (String(before[key]) !== String(draft[key])) changes.push(`${label}: ${String(before[key]) || 'None'} → ${String(draft[key]) || 'None'}`)
  }
  return changes
}

export default function AdminDiscountCodesPage() {
  const [session, setSession] = useState<SessionPayload | null>(null)
  const [data, setData] = useState<ListPayload | null>(null)
  const [selected, setSelected] = useState<DetailPayload | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [page, setPage] = useState(1)
  const [busy, setBusy] = useState(false)
  const [logoutBusy, setLogoutBusy] = useState(false)
  const [error, setError] = useState('')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editing, setEditing] = useState<DiscountCode | null>(null)
  const [draft, setDraft] = useState<Draft>(() => emptyDraft())
  const [pendingAction, setPendingAction] = useState<PendingAction>(null)
  const [ordersOpen, setOrdersOpen] = useState(false)
  const [orders, setOrders] = useState<Redemption[]>([])
  const [toasts, setToasts] = useState<AdminToast[]>([])
  const environmentLabel = useMemo(() => environmentFromHostname(window.location.hostname), [])

  function toast(title: string, message?: string) {
    const id = crypto.randomUUID()
    setToasts([{ id, title, message, tone: 'success' }])
    window.setTimeout(() => setToasts((current) => current.filter((item) => item.id !== id)), 4_000)
  }

  async function load(targetPage = page, targetSearch = search, targetStatus = status) {
    setBusy(true)
    setError('')
    try {
      const params = new URLSearchParams({ page: String(targetPage), status: targetStatus })
      if (targetSearch) params.set('search', targetSearch)
      const [sessionResponse, listResponse] = await Promise.all([
        fetch('/api/admin/session', { credentials: 'same-origin', cache: 'no-store' }),
        fetch(`/api/admin/discount-codes?${params}`, { credentials: 'same-origin', cache: 'no-store' }),
      ])
      if (sessionResponse.status === 401 || listResponse.status === 401) {
        window.location.replace('/admin/login?next=/admin/discount-codes')
        return
      }
      if (!sessionResponse.ok || !listResponse.ok) throw new Error('Discount Codes could not be loaded.')
      setSession(await sessionResponse.json() as SessionPayload)
      const nextData = await listResponse.json() as ListPayload
      setData(nextData)
      if (nextData.page !== targetPage) setPage(nextData.page)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Discount Codes could not be loaded.')
    } finally {
      setBusy(false)
    }
  }

  async function loadDetail(code: DiscountCode) {
    setError('')
    try {
      const response = await fetch(`/api/admin/discount-codes/${encodeURIComponent(code.id)}`, { credentials: 'same-origin', cache: 'no-store' })
      if (response.status === 401) {
        window.location.replace('/admin/login?next=/admin/discount-codes')
        return
      }
      if (!response.ok) throw new Error('Discount code details could not be loaded.')
      setSelected(await response.json() as DetailPayload)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Discount code details could not be loaded.')
    }
  }

  useEffect(() => { void load(page, search, status) }, [page, search, status])

  async function logout() {
    if (!session || logoutBusy) return
    setLogoutBusy(true)
    try {
      const response = await fetch('/api/admin/logout', { method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken } })
      if (!response.ok) throw new Error('Logout failed.')
      window.location.replace('/admin/login')
    } catch {
      setError('Logout could not be completed.')
      setLogoutBusy(false)
    }
  }

  function submitSearch(event: FormEvent) {
    event.preventDefault()
    setPage(1)
    setSearch(searchInput.trim())
  }

  function openCreate() {
    setEditing(null)
    setDraft(emptyDraft())
    setEditorOpen(true)
  }

  function openEdit(code: DiscountCode) {
    setEditing(code)
    setDraft(draftFromCode(code))
    setEditorOpen(true)
  }

  async function generateCode() {
    if (!session || busy) return
    setBusy(true)
    try {
      const response = await fetch('/api/admin/discount-codes/generate', {
        method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken },
      })
      const payload = await response.json().catch(() => null) as { code?: string; message?: string } | null
      if (!response.ok || !payload?.code) throw new Error(payload?.message || 'Secure code could not be generated.')
      setDraft((current) => ({ ...current, code: payload.code! }))
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Secure code could not be generated.')
    } finally {
      setBusy(false)
    }
  }

  async function saveEditor(confirmedActiveChange = false) {
    if (!session || busy) return
    const payload = payloadFromDraft(draft)
    if (!payload) {
      setError('Enter a valid discount value, dates and usage limits.')
      return
    }
    if (editing?.status === 'Active' && !confirmedActiveChange) {
      const changes = changesFor(editing, draft)
      if (changes.length) {
        setPendingAction({ kind: 'save-active', code: editing, draft, changes })
        return
      }
    }
    setBusy(true)
    setError('')
    try {
      const response = await fetch(editing ? `/api/admin/discount-codes/${encodeURIComponent(editing.id)}` : '/api/admin/discount-codes', {
        method: editing ? 'PUT' : 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': session.csrfToken },
        body: JSON.stringify(editing ? { ...payload, expectedVersion: editing.version, confirmedActiveChange } : payload),
      })
      const result = await response.json().catch(() => null) as { code?: DiscountCode; message?: string } | null
      if (!response.ok) throw new Error(result?.message || 'Discount code could not be saved.')
      setEditorOpen(false)
      setEditing(null)
      setPendingAction(null)
      toast(editing ? 'Discount code updated' : 'Discount code created', editing ? undefined : 'New discount codes start Inactive and are not customer-visible until activated.')
      await load(1, search, status)
      if (result?.code) await loadDetail(result.code)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Discount code could not be saved.')
    } finally {
      setBusy(false)
    }
  }

  async function lifecycle(kind: 'activate' | 'pause' | 'archive', code: DiscountCode) {
    if (!session || busy) return
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/discount-codes/${encodeURIComponent(code.id)}/${kind}`, {
        method: 'POST', credentials: 'same-origin', headers: { 'X-CSRF-Token': session.csrfToken },
      })
      const result = await response.json().catch(() => null) as { code?: DiscountCode; message?: string } | null
      if (!response.ok) throw new Error(result?.message || 'Discount code status could not be changed.')
      setPendingAction(null)
      toast(kind === 'activate' ? 'Discount code activated' : kind === 'pause' ? 'Discount code paused' : 'Discount code archived')
      await load(page, search, status)
      if (result?.code) await loadDetail(result.code)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Discount code status could not be changed.')
    } finally {
      setBusy(false)
    }
  }

  async function viewOrders(code: DiscountCode) {
    setBusy(true)
    setError('')
    try {
      const response = await fetch(`/api/admin/discount-codes/${encodeURIComponent(code.id)}/orders`, { credentials: 'same-origin', cache: 'no-store' })
      const payload = await response.json().catch(() => null) as { orders?: Redemption[]; message?: string } | null
      if (!response.ok || !payload?.orders) throw new Error(payload?.message || 'Discount orders could not be loaded.')
      setOrders(payload.orders)
      setOrdersOpen(true)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Discount orders could not be loaded.')
    } finally {
      setBusy(false)
    }
  }

  const columns: AdminTableColumn<DiscountCode>[] = [
    { key: 'code', label: 'Code', width: '15%', render: (row) => <button className="admin-discounts__code-link" type="button" onClick={() => void loadDetail(row)}>{row.code}</button> },
    { key: 'offer', label: 'Offer', render: (row) => <strong>{row.offer}</strong> },
    { key: 'eligibility', label: 'Eligibility', width: '22%', render: (row) => <span>{row.eligibility}</span> },
    { key: 'usage', label: 'Usage', render: (row) => <span>{row.uses}{row.totalRedemptionLimit === null ? '' : ` / ${row.totalRedemptionLimit}`}</span> },
    { key: 'starts', label: 'Starts', render: (row) => <span>{date(row.startsAt)}</span> },
    { key: 'ends', label: 'Ends', render: (row) => <span>{date(row.endsAt)}</span> },
    { key: 'status', label: 'Status', render: (row) => <AdminStatusPill tone={statusTone(row.status)}>{row.status}</AdminStatusPill> },
    { key: 'actions', label: 'Actions', align: 'right', render: (row) => <AdminButton compact variant="secondary" type="button" onClick={() => void loadDetail(row)}>View</AdminButton> },
  ]

  if (error && (!session || !data)) {
    return <main className="admin-shell-gate-error"><AdminStatePanel state="error" title="Discount Codes unavailable" description={error} action={<AdminButton onClick={() => void load()}>Retry</AdminButton>} /></main>
  }
  if (!session || !data) {
    return <main className="admin-shell-gate-loading"><AdminCard><AdminSkeleton lines={8} /></AdminCard></main>
  }

  const editorPayload = payloadFromDraft(draft)
  const current = selected?.code ?? null
  const recentRedemptions = selected?.recentRedemptions ?? []

  return (
    <AdminShell
      title="Discount Codes"
      eyebrow="OPERATIONS"
      description="Create and manage product-subtotal discount codes used by Checkout."
      user={session.user}
      onLogout={() => void logout()}
      logoutBusy={logoutBusy}
      environmentLabel={environmentLabel}
      actions={<AdminButton type="button" onClick={openCreate}>Create code</AdminButton>}
    >
      <div className="admin-discounts-page">
        {error ? <div className="admin-discounts__error" role="alert">{error}</div> : null}

        <section className="admin-discounts__summary" aria-label="Discount code summary">
          <div><span>Active codes</span><strong>{data.summary.activeCodes}</strong><small>Redeemable right now</small></div>
          <div><span>Uses this week</span><strong>{data.summary.usesThisWeek}</strong><small>Monday–Sunday · Auckland time</small></div>
          <div><span>Discount applied this week</span><strong>{money(data.summary.discountAppliedThisWeekCents)}</strong><small>Committed redemptions</small></div>
          <div><span>First-order uses this week</span><strong>{data.summary.firstOrderUsesThisWeek}</strong><small>First-order-only codes</small></div>
        </section>

        <div className="admin-discounts__layout">
          <AdminCard padding="none" className="admin-discounts__directory">
            <div className="admin-discounts__card-heading"><div><h2>Discount codes</h2><p>{data.total} configured {data.total === 1 ? 'code' : 'codes'}</p></div></div>
            <form onSubmit={submitSearch}>
              <AdminFilterBar actions={<><AdminButton type="submit">Search</AdminButton>{search ? <AdminButton variant="ghost" type="button" onClick={() => { setSearchInput(''); setPage(1); setSearch('') }}>Clear</AdminButton> : null}</>}>
                <AdminInput type="search" maxLength={64} value={searchInput} placeholder="Search code or offer" aria-label="Search discount codes" onChange={(event) => setSearchInput(event.target.value)} />
                <AdminSelect aria-label="Filter discount status" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value) }}>
                  <option value="all">All statuses</option>
                  <option value="active">Active</option>
                  <option value="scheduled">Scheduled</option>
                  <option value="inactive">Inactive</option>
                  <option value="expired">Expired</option>
                  <option value="exhausted">Exhausted</option>
                  <option value="archived">Archived</option>
                </AdminSelect>
              </AdminFilterBar>
            </form>
            {data.codes.length ? <><AdminDataTable columns={columns} rows={data.codes} rowKey={(row) => row.id} caption="Sugar Gremlin discount codes" /><div className="admin-discounts__pagination"><AdminPagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} /></div></> : <AdminStatePanel state="empty" title={search || status !== 'all' ? 'No matching discount codes' : 'No discount codes yet'} description={search || status !== 'all' ? 'Change the search or status filter.' : 'Create a code when you are ready. New codes begin Inactive.'} />}
          </AdminCard>

          <AdminCard className="admin-discounts__detail-card">
            {current ? <>
              <div className="admin-discounts__detail-heading"><div><span>SELECTED CODE</span><h2>{current.code}</h2><p>{current.offer}</p></div><AdminStatusPill tone={statusTone(current.status)}>{current.status}</AdminStatusPill></div>
              <dl className="admin-discounts__detail-list">
                <div><dt>Applies to</dt><dd>{current.appliesTo}</dd></div>
                <div><dt>Starts</dt><dd>{dateTime(current.startsAt)}</dd></div>
                <div><dt>Ends</dt><dd>{dateTime(current.endsAt)}</dd></div>
                <div><dt>First order only</dt><dd>{current.firstOrderOnly ? 'Yes' : 'No'}</dd></div>
                <div><dt>Minimum spend</dt><dd>{current.minimumSubtotalCents === null ? 'None' : money(current.minimumSubtotalCents)}</dd></div>
                <div><dt>Per-customer limit</dt><dd>{current.perCustomerLimit ?? 'No limit'}</dd></div>
                <div><dt>Total usage limit</dt><dd>{current.totalRedemptionLimit ?? 'No limit'}</dd></div>
                <div><dt>Uses</dt><dd>{current.uses}</dd></div>
                <div><dt>Remaining uses</dt><dd>{current.remainingUses ?? 'No limit'}</dd></div>
                <div><dt>Total discount applied</dt><dd>{money(current.totalDiscountAppliedCents)}</dd></div>
              </dl>
              <div className="admin-discounts__detail-actions">
                <AdminButton type="button" variant="secondary" disabled={current.status === 'Archived'} onClick={() => openEdit(current)}>Edit code</AdminButton>
                {current.enabled ? <AdminButton type="button" variant="secondary" disabled={current.status === 'Archived'} onClick={() => setPendingAction({ kind: 'pause', code: current })}>Pause</AdminButton> : <AdminButton type="button" disabled={['Archived', 'Expired', 'Exhausted'].includes(current.status)} onClick={() => setPendingAction({ kind: 'activate', code: current })}>Activate</AdminButton>}
                <AdminButton type="button" variant="secondary" onClick={() => void viewOrders(current)}>View orders</AdminButton>
                <AdminButton type="button" variant="danger" disabled={current.status === 'Archived'} onClick={() => setPendingAction({ kind: 'archive', code: current })}>Archive</AdminButton>
              </div>

              <section className="admin-discounts__recent">
                <div><h3>Recent redemptions</h3><span>{recentRedemptions.length ? 'Committed usage only' : 'No redemptions yet'}</span></div>
                {recentRedemptions.length ? <ul>{recentRedemptions.map((item) => <li key={item.id}><span><strong>{item.orderId ?? 'Order unavailable'}</strong><small>{item.customerName} · {item.committedAt ? dateTime(item.committedAt) : '—'}</small></span><b>-{money(item.discountCents)}</b></li>)}</ul> : null}
              </section>
            </> : <AdminStatePanel state="empty" title="Select a discount code" description="Choose a code from the table to review its rules, usage and actions." />}
          </AdminCard>
        </div>

        <AdminCard className="admin-discounts__info">
          <h2>How Discount Codes work</h2>
          <p>Discount Codes reduce the authoritative product subtotal only. Delivery is never discounted by a normal code. Checkout revalidates every supplied code server-side immediately before payment or order creation, and only one code can apply to an order.</p>
        </AdminCard>

        <AdminModal
          open={editorOpen}
          title={editing ? `Edit ${editing.code}` : 'Create discount code'}
          description={editing ? 'The code string is permanent. Change only the rules that should apply to new checkout attempts.' : 'New codes are created Inactive and require a separate Activate action.'}
          onClose={() => { if (!busy) setEditorOpen(false) }}
          footer={<><AdminButton variant="secondary" type="button" disabled={busy} onClick={() => setEditorOpen(false)}>Cancel</AdminButton><AdminButton type="button" disabled={busy || !editorPayload || !draft.code.trim()} onClick={() => void saveEditor()}>{busy ? 'Saving…' : editing ? 'Save changes' : 'Create inactive code'}</AdminButton></>}
        >
          <div className="admin-discounts__editor">
            <div className="admin-discounts__code-row">
              <AdminField label="Code" hint={editing ? 'Code strings cannot be changed or reused.' : 'Uppercase letters, numbers and hyphens.'}><AdminInput value={draft.code} disabled={Boolean(editing)} maxLength={32} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, code: event.target.value.toUpperCase() }))} /></AdminField>
              {!editing ? <AdminButton type="button" variant="secondary" disabled={busy} onClick={() => void generateCode()}>Generate secure code</AdminButton> : null}
            </div>
            <div className="admin-discounts__editor-grid">
              <AdminField label="Discount type"><AdminSelect value={draft.discountType} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, discountType: event.target.value as DiscountType }))}><option value="fixed">Fixed amount</option><option value="percentage">Percentage</option></AdminSelect></AdminField>
              {draft.discountType === 'fixed' ? <AdminField label="Amount (NZD)"><AdminInput inputMode="decimal" placeholder="5.00" value={draft.fixedAmount} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, fixedAmount: event.target.value }))} /></AdminField> : <AdminField label="Percentage"><AdminInput inputMode="decimal" placeholder="10" value={draft.percentage} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, percentage: event.target.value }))} /></AdminField>}
              <AdminField label="Minimum product spend" hint="Optional. Delivery never counts toward this minimum."><AdminInput inputMode="decimal" placeholder="No minimum" value={draft.minimumSubtotal} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, minimumSubtotal: event.target.value }))} /></AdminField>
              <AdminField label="Per-customer limit" hint="Optional. Uses email, mobile and card fingerprint where available."><AdminInput inputMode="numeric" placeholder="No limit" value={draft.perCustomerLimit} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, perCustomerLimit: event.target.value }))} /></AdminField>
              <AdminField label="Total usage limit"><AdminInput inputMode="numeric" placeholder="No limit" value={draft.totalRedemptionLimit} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, totalRedemptionLimit: event.target.value }))} /></AdminField>
              <label className="admin-discounts__checkbox"><input type="checkbox" checked={draft.firstOrderOnly} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, firstOrderOnly: event.target.checked }))} /><span><strong>First order only</strong><small>Rejects customers matched to a previous authoritative order or first-order claim.</small></span></label>
              <AdminField label="Starts" hint="Optional · interpreted in Pacific/Auckland"><AdminInput type="datetime-local" value={draft.startsAt} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, startsAt: event.target.value }))} /></AdminField>
              <AdminField label="Ends" hint="Optional · interpreted in Pacific/Auckland"><AdminInput type="datetime-local" value={draft.endsAt} onChange={(event) => setDraft((currentDraft) => ({ ...currentDraft, endsAt: event.target.value }))} /></AdminField>
            </div>
            <p className="admin-discounts__editor-note">Preview: <strong>{offerFromDraft(draft)}</strong> · Product subtotal only · Non-stackable</p>
          </div>
        </AdminModal>

        <AdminModal
          open={Boolean(pendingAction)}
          title={pendingAction?.kind === 'archive' ? `Archive ${pendingAction.code.code}?` : pendingAction?.kind === 'activate' ? `Activate ${pendingAction.code.code}?` : pendingAction?.kind === 'pause' ? `Pause ${pendingAction.code.code}?` : pendingAction?.kind === 'save-active' ? `Confirm changes to ${pendingAction.code.code}` : 'Confirm action'}
          description={pendingAction?.kind === 'archive' ? 'Archived codes cannot be redeemed, edited, restored or reused. Historical orders and redemptions remain unchanged.' : pendingAction?.kind === 'activate' ? 'This makes the code customer-redeemable when its configured dates and limits allow.' : pendingAction?.kind === 'pause' ? 'New checkout attempts will no longer be able to reserve this code. Existing authorized payment reservations remain bound to their frozen rule snapshot.' : pendingAction?.kind === 'save-active' ? 'This code is currently active. Review the exact material changes before saving.' : undefined}
          onClose={() => { if (!busy) setPendingAction(null) }}
          footer={pendingAction ? <><AdminButton variant="secondary" type="button" disabled={busy} onClick={() => setPendingAction(null)}>Cancel</AdminButton><AdminButton variant={pendingAction.kind === 'archive' ? 'danger' : 'primary'} type="button" disabled={busy} onClick={() => pendingAction.kind === 'save-active' ? void saveEditor(true) : void lifecycle(pendingAction.kind, pendingAction.code)}>{busy ? 'Working…' : pendingAction.kind === 'save-active' ? 'Confirm changes' : pendingAction.kind === 'archive' ? 'Archive code' : pendingAction.kind === 'activate' ? 'Activate code' : 'Pause code'}</AdminButton></> : undefined}
        >
          {pendingAction?.kind === 'save-active' ? <ul className="admin-discounts__change-list">{pendingAction.changes.map((change) => <li key={change}>{change}</li>)}</ul> : null}
        </AdminModal>

        <AdminModal open={ordersOpen} title={current ? `Orders using ${current.code}` : 'Discount orders'} description="Committed redemption records joined to authoritative Orders." onClose={() => setOrdersOpen(false)}>
          {orders.length ? <div className="admin-discounts__orders-list">{orders.map((item) => <a key={item.id} href={item.orderId ? `/admin/orders?search=${encodeURIComponent(item.orderId)}` : '/admin/orders'}><span><strong>{item.orderId ?? 'Order unavailable'}</strong><small>{item.customerName} · {item.committedAt ? dateTime(item.committedAt) : '—'}</small></span><span><b>-{money(item.discountCents)}</b><small>Total {money(item.orderTotalCents)}</small></span></a>)}</div> : <AdminStatePanel state="empty" title="No redeemed orders" description="Committed redemptions will appear here after this code is used." />}
        </AdminModal>

        <AdminToastRegion toasts={toasts} onDismiss={(id) => setToasts((currentToasts) => currentToasts.filter((item) => item.id !== id))} />
      </div>
    </AdminShell>
  )
}
